import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Building2, Users, Calendar, MoveVertical as MoreVertical, X, UserPlus, Mail, Shield, MapPin, Trash2 } from 'lucide-react';

interface Unit {
  id: string;
  name: string;
  created_at: string;
  is_active: boolean;
  admin_user_id?: string;
  user_count?: number;
}

interface UnitUser {
  id: string;
  email: string;
  role: 'seller' | 'manager' | 'motoboy';
  ruta: number;
  created_at: string;
  display_name?: string;
}
export default function UnitsManagement() {
  const { user } = useAuth();

  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitUsers, setUnitUsers] = useState<UnitUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'seller' as 'seller' | 'manager' | 'motoboy',
    ruta: 1
  });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUnits();
    }
  }, [user]);

  const fetchUnits = async () => {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Para cada unidade, contar quantos usuários estão associados
      const unitsWithUserCount = await Promise.all(
        (data || []).map(async (unit) => {
          const { count } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('unit_id', unit.id);

          return {
            ...unit,
            user_count: count || 0
          };
        })
      );

      setUnits(unitsWithUserCount);
    } catch (err) {
      console.error('Error fetching units:', err);
      setError('Erro ao carregar unidades');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersForUnit = async (unitId: string) => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, role, ruta, created_at, display_name')
        .eq('unit_id', unitId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUnitUsers(data || []);
    } catch (err) {
      console.error('Error fetching users for unit:', err);
      setError('Erro ao carregar usuários da unidade');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleManageUsers = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowUserManagementModal(true);
    fetchUsersForUnit(unit.id);
  };

  const handleCreateUserForUnit = async () => {
    if (!selectedUnit || !newUser.email || !newUser.password) return;

    setCreatingUser(true);
    try {
      // Get the current session to include the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session');
      }

      // Call the Edge Function to create the user
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newUser.email,
          password: newUser.password,
          role: newUser.role,
          ruta: newUser.ruta,
          unit_id: selectedUnit.id
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create user');
      }

      // Reset form
      setNewUser({
        email: '',
        password: '',
        role: 'seller',
        ruta: 1
      });

      // Refresh users list and units count
      await fetchUsersForUnit(selectedUnit.id);
      await fetchUnits();
    } catch (err) {
      console.error('Error creating user for unit:', err);
      setError('Erro ao criar usuário para a unidade');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!selectedUnit) return;
    if (!confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'delete', user_id: userId })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to delete user');

      await fetchUsersForUnit(selectedUnit.id);
      await fetchUnits();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Erro ao excluir usuário');
    }
  };

  const handleCreateUnit = async () => {
    if (!newUnitName.trim()) return;

    setCreating(true);
    try {
      const { error } = await supabase
        .from('units')
        .insert({
          name: newUnitName.trim(),
          is_active: true
        });

      if (error) throw error;

      setNewUnitName('');
      setShowCreateModal(false);
      await fetchUnits();
    } catch (err) {
      console.error('Error creating unit:', err);
      setError('Erro ao criar unidade');
    } finally {
      setCreating(false);
    }
  };

  const toggleUnitStatus = async (unitId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('units')
        .update({ is_active: !currentStatus })
        .eq('id', unitId);

      if (error) throw error;

      await fetchUnits();
    } catch (err) {
      console.error('Error updating unit status:', err);
      setError('Erro ao atualizar status da unidade');
    }
  };

  const handleDeleteUnit = async (unit: Unit) => {
    if (unit.user_count && unit.user_count > 0) {
      setError('Não é possível excluir uma unidade que possui usuários. Remova os usuários primeiro.');
      return;
    }
    if (!confirm(`Tem certeza que deseja excluir a unidade "${unit.name}"? Esta ação não pode ser desfeita.`)) return;

    try {
      const { error } = await supabase
        .from('units')
        .delete()
        .eq('id', unit.id);

      if (error) throw error;

      await fetchUnits();
    } catch (err: any) {
      console.error('Error deleting unit:', err);
      setError(err.message?.includes('violates foreign key')
        ? 'Não é possível excluir esta unidade pois existem dados vinculados a ela.'
        : 'Erro ao excluir unidade');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center text-red-600 p-4">
        <p>Acesso negado. Apenas administradores podem acessar esta página.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gerenciar Unidades</h1>
          <p className="text-gray-600 mt-1">Gerencie os escritórios e filiais do sistema</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nova Unidade
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {units.map((unit) => (
          <div key={unit.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">{unit.name}</h3>
                  <p className="text-sm text-gray-500">
                    Criada em {new Date(unit.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              <div className="relative">
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{unit.user_count} usuários</span>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  unit.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {unit.is_active ? 'Ativa' : 'Inativa'}
                </span>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span>ID: {unit.id.substring(0, 8)}...</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex gap-2">
                <button
                  onClick={() => toggleUnitStatus(unit.id, unit.is_active)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    unit.is_active
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {unit.is_active ? 'Desativar' : 'Ativar'}
                </button>
                <button
                  onClick={() => handleManageUsers(unit)}
                  className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                >
                  Gerenciar
                </button>
                <button
                  onClick={() => handleDeleteUnit(unit)}
                  className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Excluir unidade"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {units.length === 0 && !loading && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma unidade encontrada</h3>
          <p className="text-gray-500 mb-4">Comece criando sua primeira unidade</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nova Unidade
          </button>
        </div>
      )}

      {/* User Management Modal */}
      {showUserManagementModal && selectedUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Gerenciar Usuários</h2>
                <p className="text-gray-600">{selectedUnit.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowUserManagementModal(false);
                  setSelectedUnit(null);
                  setUnitUsers([]);
                  setNewUser({
                    email: '',
                    password: '',
                    role: 'seller',
                    ruta: 1
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Users List */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Usuários da Unidade</h3>
                </div>

                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {unitUsers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p>Nenhum usuário nesta unidade</p>
                      </div>
                    ) : (
                      unitUsers.map((unitUser) => (
                        <div key={unitUser.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {unitUser.display_name && (
                                <p className="font-semibold text-gray-900 mb-1">{unitUser.display_name}</p>
                              )}
                              <div className="flex items-center gap-2 mb-2">
                                <Mail className="w-4 h-4 text-gray-500" />
                                <span className="font-medium text-gray-900">{unitUser.email}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Shield className="w-4 h-4" />
                                  <span className="capitalize">{unitUser.role}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  <span>Ruta {unitUser.ruta}</span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                Criado em {new Date(unitUser.created_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                unitUser.role === 'manager' ? 'bg-purple-100 text-purple-800' :
                                unitUser.role === 'seller' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {unitUser.role === 'manager' ? 'Gestor' :
                                 unitUser.role === 'seller' ? 'Vendedor' : 'Motoboy'}
                              </span>
                              <button
                                onClick={() => handleDeleteUser(unitUser.id)}
                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                title="Excluir usuário"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Add New User Form */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <UserPlus className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Adicionar Novo Usuário</h3>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-mail
                      </label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="usuario@exemplo.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senha
                      </label>
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Mínimo 6 caracteres"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        minLength={6}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tipo de Usuário
                        </label>
                        <select
                          value={newUser.role}
                          onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value as 'seller' | 'manager' | 'motoboy' }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value="seller">Vendedor</option>
                          <option value="manager">Gestor</option>
                          <option value="motoboy">Motoboy</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ruta
                        </label>
                        <select
                          value={newUser.ruta}
                          onChange={(e) => setNewUser(prev => ({ ...prev, ruta: parseInt(e.target.value) }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value={1}>Ruta 1</option>
                          <option value={2}>Ruta 2</option>
                          <option value={3}>Ruta 3</option>
                          <option value={4}>Ruta 4</option>
                          <option value={5}>Ruta 5</option>
                          <option value={6}>Ruta 6</option>
                          <option value={7}>Ruta 7</option>
                          <option value={8}>Ruta 8</option>
                        </select>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <p className="text-sm text-blue-700">
                        <strong>Unidade:</strong> {selectedUnit.name}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        O usuário será automaticamente associado a esta unidade
                      </p>
                    </div>

                    <button
                      onClick={handleCreateUserForUnit}
                      disabled={!newUser.email || !newUser.password || creatingUser}
                      className={`w-full px-4 py-2 rounded-md text-white transition-colors ${
                        !newUser.email || !newUser.password || creatingUser
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {creatingUser ? 'Criando Usuário...' : 'Criar Usuário'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Create Unit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Nova Unidade</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Unidade
                </label>
                <input
                  type="text"
                  value={newUnitName}
                  onChange={(e) => setNewUnitName(e.target.value)}
                  placeholder="Ex: MasterCred - Filial São Paulo"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUnitName('');
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
                disabled={creating}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateUnit}
                disabled={!newUnitName.trim() || creating}
                className={`px-4 py-2 rounded-md text-white transition-colors ${
                  !newUnitName.trim() || creating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {creating ? 'Criando...' : 'Criar Unidade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}